import { ApprovableType, ModelApprovalable } from 'api-hooks/general/model';
import { NavigationRoutes } from 'common/constants';
import { ApiResult } from 'common/repositories/common.model';
import notification from 'components/common/notification';
import React from 'react';

import useApprovalAction, { isDataDraft } from './use-approval-action';
import useDismiss from './use-dismiss';
import useGetCrudPermission from './use-get-crud-permission';
import useNavigation from './use-navigation';

export default function useApprovalableEdit(type: ApprovableType) {
  const permission = useGetCrudPermission(type);
  const { navigate } = useNavigation();
  const { onRequestApproval } = useApprovalAction(type);
  const onDismiss = useDismiss(type);

  const editableRoute = React.useMemo(() => {
    switch (type) {
      case 'account-payables':
        return NavigationRoutes.AccountPayablesView;
      case 'account-receivables':
        return NavigationRoutes.AccountReceivablesView;
      case 'activity-purchases':
        return NavigationRoutes.ActivityPurchasesView;
      case 'activity-sales':
        return NavigationRoutes.ActivitySalesView;
      case 'balance-adjustments':
        return NavigationRoutes.BalanceAdjustmentsView;
      case 'bank-transactions':
        return NavigationRoutes.BankTransactionsView;
      case 'cash-transactions':
        return NavigationRoutes.CashTransactionsView;
      case 'cruise-purchases':
        return NavigationRoutes.CruisePurchasesView;
      case 'cruise-sales':
        return NavigationRoutes.CruiseSalesView;
      case 'document-purchases':
        return NavigationRoutes.DocumentPurchasesView;
      case 'document-sales':
        return NavigationRoutes.TravelDocumentSalesView;
      case 'flight-purchases':
        return NavigationRoutes.FlightPurchasesView;
      case 'flight-sales':
        return NavigationRoutes.TicketSalesView;
      case 'hotel-purchases':
        return NavigationRoutes.HotelPurchasesView;
      case 'hotel-sales':
        return NavigationRoutes.HotelSalesView;
      case 'order-adjustments':
        return NavigationRoutes.OrderAdjustmentView;
      case 'product-purchases':
        return NavigationRoutes.ProductPurchasesView;
      case 'product-sales':
        return NavigationRoutes.ProductSalesView;
      case 'receipts':
        return NavigationRoutes.ReceiptsView;
      case 'tour-sales':
        return NavigationRoutes.ToursalesView;
      case 'transfers':
        return NavigationRoutes.TransfersView;
      case 'tours':
        return NavigationRoutes.TourView;
      case 'refunds':
        return NavigationRoutes.RefundView;
      case 'release-purchases':
        return NavigationRoutes.RefundReleaseAgentView;
      case 'release-sales':
        return NavigationRoutes.RefundReleaseUserView;
      case 'projects':
        return NavigationRoutes.ProjectView;
      case 'other-expenses':
      case 'other-incomes':
      default:
        return undefined;
    }
  }, [type]);

  const onNavigate = React.useCallback(
    (id: string) => {
      if (!permission.view) return;
      if (!editableRoute) return;
      navigate(editableRoute, {
        id,
      });
    },
    [editableRoute, navigate, permission.view],
  );

  const onResolve = React.useCallback(
    (result: ApiResult<ModelApprovalable>) => {
      const data = result.data;
      if (editableRoute) {
        onNavigate(data.id);
        const isDraft = isDataDraft(data.status);
        if (isDraft) {
          onRequestApproval(data.id); // has dismiss if succeed
        } else {
          onDismiss(data.id);
        }
        notification.success({
          message: result.message,
        });
      } else {
        onDismiss(data.id);
      }
    },
    [editableRoute, onDismiss, onNavigate, onRequestApproval],
  );

  return onResolve;
}
